"use client";
// The planning board: Ziele on top, Anforderungen | Abnahmekriterien, Features with their Bausteine, then the
// open questions. A click selects an entry and marks its chain everywhere; Enter or a double click edits it.
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { toggleLink } from "./actions.ts";
import { byArea, type Board as BoardData, type Kind } from "@/lib/types.ts";
import { buildGraph, key, parseKey, type Key } from "./graph.ts";
import { Editor, type EditTarget } from "./Editor.tsx";
import { Tip, featurePriority, type TipAt } from "./Tip.tsx";

type Mode = "hl" | "filter";

const ORDER: Kind[] = [
  "goal",
  "measure",
  "requirement",
  "criterion",
  "feature",
  "question",
];

export function Board({ board }: { board: BoardData }) {
  const graph = useMemo(() => buildGraph(board), [board]);
  const [selected, setSelected] = useState<Key | null>(null);
  const [mode, setMode] = useState<Mode>("hl");
  const [editing, setEditing] = useState<EditTarget | null>(null);

  // text size per browser, kept in localStorage (may be blocked: then it just resets on reload)
  const [scale, setScale] = useState(1);
  useEffect(() => {
    try {
      const v = Number(localStorage.getItem("blueprint-scale"));
      // eslint-disable-next-line react-hooks/set-state-in-effect -- read once after hydration
      if (v >= 0.7 && v <= 1.6) setScale(v);
    } catch {}
  }, []);
  const zoom = (d: number | null) =>
    setScale((s) => {
      const v =
        d === null
          ? 1
          : Math.round(Math.min(1.6, Math.max(0.7, s + d)) * 10) / 10;
      try {
        localStorage.setItem("blueprint-scale", String(v));
      } catch {}
      return v;
    });

  // a deleted entry can't stay selected
  const current = selected && graph.title(selected) ? selected : null;

  // text search over everything (titles, descriptions, answers, Bausteine, numbers); hits are marked in the titles
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const searchRef = useRef<HTMLInputElement>(null);
  const hits = useMemo(() => {
    if (!q) return null;
    const out = new Set<Key>();
    const test = (k: Key, ...texts: (string | null | undefined)[]) => {
      if ([graph.label(k), ...texts].some((t) => t?.toLowerCase().includes(q)))
        out.add(k);
    };
    for (const e of board.goals)
      test(key("goal", e.id), e.title, e.description);
    for (const e of board.measures)
      test(key("measure", e.id), e.title, e.description);
    for (const e of board.requirements)
      test(key("requirement", e.id), e.title, e.description);
    for (const e of board.criteria)
      test(key("criterion", e.id), e.title, e.description);
    for (const e of board.features)
      test(
        key("feature", e.id),
        e.title,
        e.description,
        ...board.bausteine
          .filter((b) => b.featureId === e.id)
          .map((b) => b.title),
      );
    for (const e of board.questions)
      test(key("question", e.id), e.title, e.description, e.answer, e.topic);
    return out;
  }, [q, board, graph]);
  /** Marks the search term in a text. */
  const hl = (text: string) => {
    if (!q) return text;
    const parts: React.ReactNode[] = [];
    const lower = text.toLowerCase();
    let i = 0;
    for (let j = lower.indexOf(q); j >= 0; j = lower.indexOf(q, i)) {
      parts.push(
        text.slice(i, j),
        <mark key={j}>{text.slice(j, j + q.length)}</mark>,
      );
      i = j + q.length;
    }
    parts.push(text.slice(i));
    return parts;
  };
  const rel = useMemo(
    () => (current ? graph.related(current) : new Set<Key>()),
    [current, graph],
  );

  // Anforderungen: main requirements (Bereiche) with their items, which fold out like Bausteine: under the
  // selected Bereich or item, and wherever an item is part of the selection's chain or matches the search
  const reqGroups = useMemo(
    () =>
      board.requirements
        .filter((r) => r.parentId === null)
        .map((main) => {
          const items = board.requirements.filter((r) => r.parentId === main.id);
          const mk = key("requirement", main.id);
          const open =
            current === mk ||
            items.some((r) => {
              const k = key("requirement", r.id);
              return current === k || rel.has(k) || !!hits?.has(k);
            });
          return { main, items, open };
        }),
    [board, current, rel, hits],
  );
  const idsOf = useMemo(
    (): Record<Kind, number[]> => ({
      goal: board.goals.map((e) => e.id),
      measure: board.measures.map((e) => e.id),
      requirement: reqGroups.flatMap((g) => [g.main.id, ...(g.open ? g.items.map((e) => e.id) : [])]),
      criterion: board.criteria.map((e) => e.id),
      feature: board.features.map((e) => e.id),
      // questions are shown grouped by topic, so the arrow keys follow that order
      question: byArea(
        board.questions.map((q) => ({ ...q, area: q.topic })),
      ).flatMap(([, list]) => list.map((e) => e.id)),
    }),
    [board, reqGroups],
  );

  // first click selects, a click on the selected entry opens it; Esc or a click on empty space clears
  const clickRow = (k: Key) => (k === current ? startEdit(k) : setSelected(k));

  // Shift-click links the clicked entry to the selected one, or removes the link; the selection stays.
  const [note, setNote] = useState<{ text: string; error?: boolean } | null>(
    null,
  );
  const [linking, startLinking] = useTransition();
  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), note.error ? 5000 : 2500);
    return () => clearTimeout(t);
  }, [note]);
  const link = (k: Key) => {
    if (!current)
      return setNote({
        text: "Erst einen Eintrag wählen, dann Shift-Klick auf den zweiten",
        error: true,
      });
    if (k === current || linking) return;
    const [ak, aid] = parseKey(current);
    const [bk, bid] = parseKey(k);
    const pair = `${graph.label(current)} ↔ ${graph.label(k)}`;
    startLinking(async () => {
      try {
        const res = await toggleLink(
          { kind: ak, id: aid },
          { kind: bk, id: bid },
        );
        if (!res.ok) return setNote({ text: res.message, error: true });
        const r = res.result;
        // criteria and Messgrößen are the ones that move to the clicked parent
        const moves = ak === "criterion" || ak === "measure";
        setNote({
          text:
            r === "verschoben"
              ? `${graph.label(moves ? current : k)} hängt jetzt an ${graph.label(moves ? k : current)}`
              : `${pair} ${r}`,
        });
      } catch (err) {
        setNote({
          text: err instanceof Error ? err.message : String(err),
          error: true,
        });
      }
    });
  };
  const startEdit = (k: Key) => {
    const [kind, id] = parseKey(k);
    setSelected(k);
    setEditing({ kind, id });
  };
  const startNew = (kind: Kind) =>
    setEditing({ kind, id: null, context: current });

  useEffect(() => {
    if (editing) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest("input, textarea, select, [contenteditable]")) return;
      if (e.key === "/") {
        e.preventDefault();
        return searchRef.current?.focus();
      }
      // Esc clears the selection first, then the search
      if (e.key === "Escape") return current ? setSelected(null) : setQuery("");
      if (e.key === "Enter" && current) {
        e.preventDefault();
        return startEdit(current);
      }
      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key))
        return;
      e.preventDefault();
      if (!current) {
        const first = ORDER.find((k) => idsOf[k].length);
        if (first) setSelected(key(first, idsOf[first][0]));
        return;
      }
      const [kind, id] = parseKey(current);
      let next: Key | undefined;
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        const list = idsOf[kind];
        const i = list.indexOf(id) + (e.key === "ArrowDown" ? 1 : -1);
        if (i >= 0 && i < list.length) next = key(kind, list[i]);
      } else {
        const step = e.key === "ArrowRight" ? 1 : -1;
        for (
          let t = ORDER.indexOf(kind) + step;
          t >= 0 && t < ORDER.length;
          t += step
        ) {
          const list = idsOf[ORDER[t]];
          if (!list.length) continue;
          const linked = list.find((i) => rel.has(key(ORDER[t], i)));
          next = key(ORDER[t], linked ?? list[0]);
          break;
        }
      }
      if (next) {
        setSelected(next);
        requestAnimationFrame(() =>
          document
            .querySelector(`[data-key="${next}"]`)
            ?.scrollIntoView({ block: "nearest" }),
        );
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // tooltip for symbols (elements with data-tip), after a short pause; gone on leave, click or scroll
  const [tip, setTip] = useState<TipAt | null>(null);
  const tipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tipEl = useRef<Element | null>(null);
  const hideTip = () => {
    if (tipTimer.current) clearTimeout(tipTimer.current);
    tipEl.current = null;
    setTip(null);
  };
  useEffect(() => {
    window.addEventListener("scroll", hideTip, true);
    return () => window.removeEventListener("scroll", hideTip, true);
  }, []);
  const onTipOver = (e: React.MouseEvent) => {
    const el = (e.target as Element).closest("[data-tip]");
    if (el === tipEl.current) return;
    hideTip();
    if (!el) return;
    tipEl.current = el;
    tipTimer.current = setTimeout(() => {
      const r = el.getBoundingClientRect();
      setTip({
        text: el.getAttribute("data-tip") ?? "",
        left: r.left,
        top: r.top,
        bottom: r.bottom,
      });
    }, 200);
  };

  const rowProps = (k: Key) => {
    const state =
      k === current ? "sel" : rel.has(k) ? "rel" : current ? "other" : "";
    const miss = hits && !hits.has(k) ? " miss" : "";
    return {
      "data-key": k,
      className: `row ${state}${miss}`,
      onClick: (e: React.MouseEvent) => {
        hideTip();
        if (e.shiftKey) link(k);
        else clickRow(k);
      },
      onDoubleClick: () => startEdit(k),
    };
  };
  // goals and Messgrößen the client has confirmed; drafts carry no mark
  const agreedSym = (
    <span
      className="ok"
      data-tip={`Mit ${board.project.client ?? "dem Kunden"} abgestimmt`}
    >
      ✓
    </span>
  );
  const qSym = (k: Key) => {
    const n = graph.openQuestions.get(k);
    if (!n) return null;
    const open = graph
      .questionsOf(k)
      .filter(
        (id) => board.questions.find((q) => q.id === id)?.status === "offen",
      )
      .map(
        (id) =>
          `${graph.label(key("question", id))} ${graph.title(key("question", id))}`,
      );
    return (
      <span
        className="qs"
        data-tip={`${n === 1 ? "1 offene Frage" : `${n} offene Fragen`}\n${open.join("\n")}`}
      >
        ?{n}
      </span>
    );
  };
  const head = (title: string, count: number, kind: Kind, lead?: string) => (
    <>
      <h2>
        <span>
          <span className="count">{count}</span> {title}
        </span>
        <button
          className="add"
          onClick={() => startNew(kind)}
          title="Neuer Eintrag"
        >
          + Neu
        </button>
      </h2>
      {lead && <p className="lead">{lead}</p>}
    </>
  );
  const editBtn = (k: Key) =>
    k === current ? (
      <button
        className="edit"
        title="Bearbeiten (Enter)"
        onClick={(e) => {
          e.stopPropagation();
          startEdit(k);
        }}
      >
        ✎
      </button>
    ) : null;

  const topics = [
    ...new Set(board.questions.map((q) => q.topic ?? "Ohne Thema")),
  ];

  return (
    <>
      <div
        className={`page mode-${mode} ${current ? "active" : ""} ${hits ? "searching" : ""}`}
        style={{ zoom: scale }}
        onMouseOver={onTipOver}
        onMouseLeave={hideTip}
        onClick={(e) => {
          // a click on empty space (background, panel padding) clears the selection
          if (
            !(e.target as Element).closest(
              ".row, button, a, input, select, textarea, label, .editor-backdrop",
            )
          )
            setSelected(null);
        }}
      >
        <header className="top">
          <div>
            <h1>{board.project.name}</h1>
            {board.project.client && (
              <p className="sub">{board.project.client}</p>
            )}
          </div>
          <div className="modes">
            <span className="search">
              <input
                ref={searchRef}
                type="search"
                placeholder="Suchen … ( / )"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setQuery("");
                    e.currentTarget.blur();
                  }
                }}
              />
              {hits && <span className="hits">{hits.size} Treffer</span>}
            </span>
            <button
              className={mode === "hl" ? "on" : ""}
              onClick={() => setMode("hl")}
            >
              Hervorheben
            </button>
            <button
              className={mode === "filter" ? "on" : ""}
              onClick={() => setMode("filter")}
            >
              Filtern
            </button>
            <span className="zoom">
              <button
                onClick={() => zoom(-0.1)}
                title="Schrift kleiner"
                disabled={scale <= 0.7}
              >
                −
              </button>
              <button
                onClick={() => zoom(null)}
                title="Normale Größe"
                className="pct"
              >
                {Math.round(scale * 100)} %
              </button>
              <button
                onClick={() => zoom(0.1)}
                title="Schrift größer"
                disabled={scale >= 1.6}
              >
                +
              </button>
            </span>
            <span className="legend">
              <span className="stripe Muss" />
              Muss <span className="stripe Soll" />
              Soll <span className="stripe Kann" />
              Kann · <Icon name="criterion" /> Kriterium fehlt ·{" "}
              <Icon name="feature" /> Feature fehlt · <Icon name="measure" />{" "}
              Messgröße fehlt · <span className="qs">?2</span> offene Fragen ·{" "}
              <span className="ok-legend">✓</span> abgestimmt
            </span>
            <span className="hint">
              ↑↓ Zeile · ←→ Tabelle · Enter bearbeiten · Shift-Klick
              verknüpfen/trennen · Esc
            </span>
          </div>
        </header>

        <div className="two">
          <section className="panel goals">
            {head(
              "Ziele",
              board.goals.length,
              "goal",
              "Was der Kunde mit dem Projekt erreichen will: das Warum, unabhängig von der Umsetzung.",
            )}
            {board.goals.map((g) => {
              const k = key("goal", g.id);
              const noMeasure = !(graph.measuresOfGoal.get(g.id) ?? []).length;
              return (
                <div key={k} {...rowProps(k)}>
                  <span className="id">Z{g.num}</span>
                  <span className="t">{hl(g.title)}</span>
                  <span className="sym">
                    {qSym(k)}
                    {g.status === "abgestimmt" && agreedSym}
                    {noMeasure && (
                      <Icon name="measure" label="Messgröße fehlt" />
                    )}
                  </span>
                  {editBtn(k)}
                </div>
              );
            })}
          </section>
          <section className="panel">
            {head(
              "Messgrößen",
              board.measures.length,
              "measure",
              "Woran man nach der Einführung prüft, ob ein Ziel erreicht ist. Zeigt den Erfolg, ist nicht Teil der Abnahme.",
            )}
            {board.measures.length === 0 && (
              <p className="empty">
                Noch keine Messgrößen. Ziel wählen, dann „+ Neu“.
              </p>
            )}
            {board.measures.map((m) => {
              const k = key("measure", m.id);
              const goal = board.goals.find((g) => g.id === m.goalId);
              return (
                <div key={k} {...rowProps(k)}>
                  <span className="id">M{m.num}</span>
                  <span className="t">{hl(m.title)}</span>
                  <span className="sym">
                    {qSym(k)}
                    {m.status === "abgestimmt" && agreedSym}
                    <span
                      className="ref"
                      data-tip={goal ? `Z${goal.num} ${goal.title}` : undefined}
                    >
                      Z{goal?.num}
                    </span>
                  </span>
                  {editBtn(k)}
                </div>
              );
            })}
          </section>
        </div>

        <div className="three">
          <section className="panel">
            {head("Anforderungen", board.requirements.length, "requirement")}
            {reqGroups.map(({ main, items, open }) => {
              const mk = key("requirement", main.id);
              const prios = new Set(items.map((r) => r.priority));
              const prio = prios.has("Muss") ? "Muss" : prios.has("Soll") ? "Soll" : prios.has("Kann") ? "Kann" : undefined;
              const noFeature = items.filter((r) => !(graph.featuresOfReq.get(r.id) ?? []).length).length;
              const noCriterion = items.filter(
                (r) => r.priority === "Muss" && !(graph.criteriaOfReq.get(r.id) ?? []).length,
              ).length;
              return (
                <div key={mk} className="topic req-group">
                  <div {...rowProps(mk)} data-p={prio} data-main="">
                    <span className="id">{graph.label(mk)}</span>
                    <span className="t">{hl(main.title)}</span>
                    <span className="sym">
                      {qSym(mk)}
                      {noCriterion > 0 && (
                        <Icon name="criterion" label={`${noCriterion} ohne Abnahmekriterium`} />
                      )}
                      {noFeature > 0 && <Icon name="feature" label={`${noFeature} ohne Feature`} />}
                      <span data-tip={`${items.length} Unteranforderungen`}>{items.length}</span>
                    </span>
                    {editBtn(mk)}
                  </div>
                  {open &&
                    items.map((r) => {
                      const k = key("requirement", r.id);
                      const noF = !(graph.featuresOfReq.get(r.id) ?? []).length;
                      const noC = r.priority === "Muss" && !(graph.criteriaOfReq.get(r.id) ?? []).length;
                      return (
                        <div key={k} {...rowProps(k)} data-p={r.priority} data-item="">
                          <span className="id">{graph.label(k)}</span>
                          <span className="t">{hl(r.title)}</span>
                          <span className="sym">
                            {qSym(k)}
                            {noC && <Icon name="criterion" label="Kriterium fehlt" />}
                            {noF && <Icon name="feature" label="Feature fehlt" />}
                          </span>
                          {editBtn(k)}
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </section>
          <section className="panel">
            {head("Abnahmekriterien", board.criteria.length, "criterion")}
            {board.criteria.length === 0 && (
              <p className="empty">
                Noch keine Abnahmekriterien. Anforderung wählen, dann „+ Neu“.
              </p>
            )}
            {board.criteria.map((c) => {
              const k = key("criterion", c.id);
              const req = board.requirements.find(
                (r) => r.id === c.requirementId,
              );
              return (
                <div key={k} {...rowProps(k)}>
                  <span className="id">K{c.num}</span>
                  <span className="t">{hl(c.title)}</span>
                  <span className="sym">
                    {qSym(k)}
                    <span
                      className="ref"
                      data-tip={req ? `${graph.label(key("requirement", req.id))} ${req.title}` : undefined}
                    >
                      {req ? graph.label(key("requirement", req.id)) : "A?"}
                    </span>
                    {c.status === "erfüllt" && (
                      <span className="ok" data-tip="erfüllt">
                        ✓
                      </span>
                    )}
                    {c.status === "nicht erfüllt" && (
                      <span className="bad" data-tip="nicht erfüllt">
                        ✗
                      </span>
                    )}
                  </span>
                  {editBtn(k)}
                </div>
              );
            })}
          </section>
          <section className="panel features">
            {head("Features", board.features.length, "feature")}
            {board.features.map((f) => {
              const k = key("feature", f.id);
              const reqs = (graph.reqsOfFeature.get(f.id) ?? []).map((r) =>
                graph.label(key("requirement", r)),
              );
              const parts = board.bausteine.filter((b) => b.featureId === f.id);
              // Bausteine show under the selected feature, and under features whose Bausteine match the search
              const open =
                k === current ||
                (!!q && parts.some((b) => b.title.toLowerCase().includes(q)));
              return (
                <div key={k} className="feature-block">
                  <div
                    {...rowProps(k)}
                    data-p={featurePriority(graph, board, f.id) ?? undefined}
                  >
                    <span className="id">F{f.num}</span>
                    <span className="t">{hl(f.title)}</span>
                    <span className="sym">
                      {qSym(k)}
                      {!reqs.length && (
                        <span
                          className="bad"
                          data-tip="Deckt keine Anforderung"
                        >
                          !
                        </span>
                      )}
                      {f.status === "in Arbeit" && (
                        <span className="busy" data-tip="in Arbeit">
                          ◐
                        </span>
                      )}
                      {f.status === "fertig" && (
                        <span className="ok" data-tip="fertig">
                          ✓
                        </span>
                      )}
                      {parts.length > 0 && (
                        <span data-tip={`${parts.length} Bausteine`}>
                          {parts.length}
                        </span>
                      )}
                    </span>
                    {editBtn(k)}
                  </div>
                  {open && parts.length > 0 && (
                    <ol className="bs">
                      {parts.map((b, i) => (
                        <li key={b.id}>
                          <span className="id">
                            F{f.num}.{i + 1}
                          </span>
                          {hl(b.title)}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              );
            })}
          </section>
        </div>

        <section className="panel wide questions">
          {head(
            "Offene Fragen",
            board.questions.filter((q) => q.status === "offen").length,
            "question",
          )}
          {topics.map((topic) => (
            <div key={topic} className="topic">
              <h3>{topic}</h3>
              {board.questions
                .filter((q) => (q.topic ?? "Ohne Thema") === topic)
                .map((q) => {
                  const k = key("question", q.id);
                  const links = [...graph.related(k)]
                    .sort(
                      (a, b) =>
                        ORDER.indexOf(parseKey(a)[0]) -
                        ORDER.indexOf(parseKey(b)[0]),
                    )
                    .map((l) => graph.label(l));
                  return (
                    <div key={k} {...rowProps(k)}>
                      <span className="id">
                        {q.important && (
                          <span data-tip="Für den nächsten Termin wichtig">
                            ★
                          </span>
                        )}
                        Q{q.num}
                      </span>
                      <span
                        className={`t ${q.status === "beantwortet" ? "done" : ""}`}
                      >
                        {hl(q.title)}
                      </span>
                      <span className="m">
                        {(q.answer && hl(q.answer)) ?? (
                          <em className="none">noch keine Antwort</em>
                        )}
                      </span>
                      <span className="n">
                        {links.join(" · ")}
                        {editBtn(k)}
                      </span>
                    </div>
                  );
                })}
            </div>
          ))}
        </section>

        {note && (
          <div className={`note ${note.error ? "err" : ""}`} role="status">
            {note.text}
          </div>
        )}

        {editing && (
          <Editor
            key={`${editing.kind}:${editing.id}`}
            onOpen={(kind, id) => {
              setSelected(key(kind, id));
              setEditing({ kind, id });
            }}
            board={board}
            graph={graph}
            target={editing}
            onClose={() => setEditing(null)}
            onSaved={(kind, id) => {
              setEditing(null);
              setSelected(key(kind, id));
            }}
            onDeleted={() => {
              setEditing(null);
              setSelected(null);
            }}
          />
        )}
      </div>
      {tip && !editing && <Tip at={tip} scale={scale} />}
    </>
  );
}

/** Small line icons for the gap markers (red via CSS); the hover card says what they mean. */
function Icon({
  name,
  label,
}: {
  name: "criterion" | "feature" | "measure";
  label?: string;
}) {
  return (
    <svg
      className="icon gapi"
      viewBox="0 0 24 24"
      width="14"
      height="14"
      aria-label={label}
      data-tip={label}
      role={label ? "img" : undefined}
    >
      {name === "criterion" ? (
        <path d="M10 6h10M10 12h10M10 18h10M3.5 6l1.5 1.5L7.5 5M3.5 12l1.5 1.5 2.5-2.5M3.5 18l1.5 1.5 2.5-2.5" />
      ) : name === "feature" ? (
        <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3zM12 12l8-4.5M12 12v9M12 12L4 7.5" />
      ) : (
        <path d="M4 16a8 8 0 1 1 16 0M12 16l4-5M12 16h.01" />
      )}
    </svg>
  );
}
